import calulateIcon from '~/assets/images/icon-calculate.png';
import compareIcon from '~/assets/images/icon-compare.png';
import searchAdvisorIcon from '~/assets/images/icon-searchAdvisor.png';

export type Tile = {
  icon: string;
  title: string;
  content: string;
};

const tiles: Tile[] = [
  {
    icon: calulateIcon,
    title: 'Berekenen',
    content: 'Ontdek in een paar klikken wat je maximale hypotheek is.',
  },
  {
    icon: compareIcon,
    title: 'Vergelijk',
    content:
      'Bekijk en vergelijk hypotheekvormen, rentepercentages en maandlasten.',
  },
  {
    icon: searchAdvisorIcon,
    title: 'Zoek een adviseur',
    content: 'Vind een onafhankelijk hypotheekadviseur bij jou in de buurt.',
  },
];

export type FaqItem = {
  title: string;
  content: string;
};

const faqItems: FaqItem[] = [
  {
    title: 'De hoogte van je inkomen',
    content:
      'Je inkomen is de belangrijkste factor bij het berekenen van je maximale hypotheek. Hoe hoger je vaste inkomen, hoe meer je kunt lenen. Ook eventuele bonussen of partnerinkomen kunnen meetellen.',
  },
  {
    title: 'Bestaande schulden en leningen',
    content:
      'Heb je lopende leningen, een creditcardschuld of bijvoorbeeld een studieschuld? Dan kan dat je maximale hypotheekbedrag verlagen, omdat het invloed heeft op wat je maandelijks kunt dragen.',
  },
  {
    title: 'Leeftijd',
    content:
      'Je leeftijd bepaalt deels de looptijd van je hypotheek. Jongere aanvragers kunnen vaak langer lenen; bij een hogere leeftijd kan de looptijd korter zijn, waardoor het maximale bedrag lager uitvalt.',
  },
  {
    title: 'Dienstverband en inkomenszekerheid',
    content:
      'Geldverstrekkers kijken naar de zekerheid van je inkomen. Een vast contract biedt meer stabiliteit, maar ook met een tijdelijk contract of als zzp’er kun je vaak een hypotheek krijgen, mits je inkomen stabiel is.',
  },
  {
    title: 'Toetsrente',
    content:
      'De toetsrente is de rente waarmee wordt getoetst of je de hypotheek kunt betalen. Als deze rente stijgt, daalt je maximale leencapaciteit. Daarom verschilt je maximale hypotheek soms per renteaanbieder.',
  },
  {
    title: 'Nationale Hypotheek Garantie (NHG)',
    content:
      'NHG is een factor die je maximale hypotheek kan beïnvloeden, omdat het een maximale kostengrens stelt en een rentekorting mogelijk maakt. De lagere rente bij NHG zorgen ervoor dat je mogelijk meer kunt lenen binnen de NHG-limieten.',
  },
  {
    title: 'Waarde van de woning',
    content:
      'Je mag in Nederland meestal maximaal 100% van de woningwaarde lenen. Wil je extra’s meefinancieren, zoals een verbouwing, dan moet dat vaak met eigen geld of subsidies worden gedekt.',
  },
  {
    title: 'Eigen geld of spaargeld',
    content:
      'Hoe meer eigen geld je kunt inbrengen, hoe minder je hoeft te lenen. Spaargeld wordt vaak gebruikt voor bijkomende kosten, zoals overdrachtsbelasting, notariskosten of een verbouwing.',
  },
  {
    title: 'Energielabel en verduurzaming',
    content:
      'Een woning met een goed energielabel kan leiden tot een hogere leencapaciteit. Sommige banken bieden extra leenruimte voor duurzame verbeteringen, zoals isolatie of zonnepanelen.',
  },
  {
    title: 'Rentepercentage / marktrente',
    content:
      'De actuele hypotheekrente bepaalt direct wat je maandlasten zijn, en dus hoeveel je kunt lenen. Een lage rente betekent lagere maandlasten en een hogere maximale hypotheek.',
  },
  {
    title: 'Advies en begeleiding',
    content:
      'Een berekening is een goede eerste stap, maar persoonlijk advies maakt het verschil. Een erkend hypotheekadviseur helpt je de cijfers te vertalen naar keuzes die écht bij jouw situatie passen.',
  },
];

export type MortgageType = {
  title: string;
  description: string;
  pros: string[];
  cons: string[];
};

const mortgageTypes: MortgageType[] = [
  {
    title: 'Annuïteit',
    description:
      'Vast bruto maandbedrag (rente + aflossing). In het begin meer rente, later meer aflossing',
    pros: [
      'Vaste bruto maandlasten',
      'Lagere netto lasten in de eerste jaren',
      'Geschikt als je lasten in de startfase laag wilt houden',
    ],
    cons: [
      'Totale rentelasten hoger dan bij lineair',
      'Netto lasten stijgen na verloop van tijd',
    ],
  },
  {
    title: 'Lineair',
    description:
      'Vaste aflossing per maand + rente over hypotheekschuld. Maandlasten dalen gedurende de looptijd',
    pros: [
      'Snellere aflossing',
      'Totale rentelasten lager dan bij annuïtair',
      'Maandlasten nemen af',
    ],
    cons: [
      'Hogere lasten in de eerste jaren',
      'Minder toegankelijk voor lagere inkomens',
    ],
  },
  {
    title: 'Aflossingsvrij',
    description:
      'Alleen rente, geen aflossing. Schuld blijft staan tot einde looptijd',
    pros: ['Lage maandlasten', 'Meer financiële ruimte op korte termijn'],
    cons: [
      'Schuld blijft gelijk',
      'Sinds 2013 geen renteaftrek meer voor nieuwe hypotheken',
      'Risico op restschuld',
      'Aan het eind in 1 keer aflossen',
    ],
  },
];

const callToAction = {
  title: 'Meer weten over je maximale hypotheek?',
  getContent(activeOffices: number) {
    return `Je weet nu ongeveer wat je maximale hypotheek is, maar dat is pas het begin. Hoeveel je écht kunt lenen, hangt ook af van je persoonlijke situatie, woonwensen en toekomstplannen. Een hypotheekadviseur kan je helpen om verder te kijken dan de cijfers en inzicht geven in wat er financieel mogelijk én verstandig is.\n
       Bij Advieskeuze vind je altijd de juiste adviseur: ${activeOffices.toLocaleString('nl-NL')} staan vermeld op ons platform. Vergelijk eenvoudig op reviews, tarieven en keurmerken en kies de adviseur die het best bij jou past.`;
  },
  buttonTitle: 'Zoek een hypotheekadviseur',
  link: '/hypotheek',
};

export const textContent = {
  tiles,
  faqItems,
  mortgageTypes,
  callToAction,
};
